import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Group, Header, Calendar, dayGridPlugin } from '@salecycle/ui-component-library';
import moment from 'moment';
import Spinner from 'core/ui/components/spinner';
import { ICampaign } from 'entities/campaign/campaign';
import { connect } from 'react-redux';
import { Actions as RouterActions, Selectors as Router } from 'router';
import { State } from 'store';
import { Selectors } from '../../state/campaigns';
import PageWrapper from '../components/PageWrapper';
import { Helpers } from 'entities/lifecycle';
import { campaignActivityStatesConfig } from '../utils/campaignActivityStatesConfig';

export interface IStateProps {
  siteId: string;
  campaigns: ICampaign[];
  refreshing: boolean;
}

interface IDispatchProps {
  push: (href: string) => void;
}

type IProps = IStateProps & IDispatchProps;

const eventMomentFormat = 'YYYY-MM-DD';

export const CampaignsList: FC<IProps> = props => {
  const { t } = useTranslation('campaigns');

  const newCampaign = () => props.push(`/sites/${props.siteId}/campaigns/campaigns/newcampaign/objective`);

  const filteredCampaigns = props.campaigns.filter(
    campaign =>
      Helpers.getActivityState(campaign) === 'Live' ||
      Helpers.getActivityState(campaign) === 'Scheduled' ||
      Helpers.getActivityState(campaign) === 'Testing' ||
      Helpers.getActivityState(campaign) === 'Migration',
  );

  const campaignEvents = filteredCampaigns.map(campaign => {
    const { text, color } = campaignActivityStatesConfig(t)[Helpers.getActivityState(campaign)];

    return {
      id: campaign.id,
      title: `${campaign.name} (${text})`,
      start: moment(campaign.startTime).format(eventMomentFormat),
      end: moment(campaign.endTime).format(eventMomentFormat),
      backgroundColor: color,
      borderColor: color,
    };
  });

  return (
    <PageWrapper>
      <Box stackItems={true}>
        <Header heading={t('campaigns:heading')}>
          <Group>
            <Button variation="primary" onClick={newCampaign}>
              {t('campaigns:newCampaign')}
            </Button>
          </Group>
        </Header>

        {props.campaigns.length > 0 && !props.refreshing ? (
          <Calendar defaultView="dayGridMonth" plugins={[dayGridPlugin]} events={campaignEvents} eventLimit={true} />
        ) : props.refreshing ? (
          <Spinner />
        ) : (
          <Box stackItems={true} horizontalPositioning="center" padding="40px 0">
            <h3>{t('campaigns:noCampaigns')}</h3>
          </Box>
        )}
      </Box>
    </PageWrapper>
  );
};

function mapState(state: State): IStateProps {
  const siteId = Router.getSiteId(state);
  const campaigns = Selectors.getEntityIds(state).map(id => ({ id, ...Selectors.getEntity(state, id) }));

  return {
    refreshing: Selectors.isRefreshing(state),
    siteId,
    campaigns,
  };
}

const actionCreators: IDispatchProps = {
  push: RouterActions.push,
};

export default connect(mapState, actionCreators)(CampaignsList);
